import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AboutPage from '@/pages/AboutPage';
import ForSchoolsPage from '@/pages/ForSchoolsPage';
import ContactPage from '@/pages/ContactPage';
import AdminDashboard from '@/pages/AdminDashboard';
import CoordinatorDashboard from '@/pages/CoordinatorDashboard';
import MentorDetailsPage from '@/pages/coordinator/MentorDetailsPage';
import ParentDashboard from '@/pages/ParentDashboard';
import MentorHome from '@/pages/mentor/MentorHome';
import MentorExperiencesPage from '@/pages/mentor/MentorExperiencesPage';
import MentorProfilePage from '@/pages/mentor/MentorProfilePage';
import ChildJourneyPage from '@/pages/ChildJourneyPage';
import ExploreCareersPage from '@/pages/ExploreCareersPage';
import CareerDetailPage from '@/pages/CareerDetailPage';
import MentorOnboardingPage from '@/pages/MentorOnboardingPage';
import BookingFlowPage from '@/pages/BookingFlowPage';
import VolunteerPage from '@/pages/VolunteerPage';
import SessionDetailPage from '@/pages/SessionDetailPage';
import GroupSessionDetailPage from '@/pages/GroupSessionDetailPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import StudentSafetyPage from '@/pages/StudentSafetyPage';
import SchoolRegistrationPage from '@/pages/SchoolRegistrationPage';
import BringToSchoolPage from '@/pages/BringToSchoolPage';
import CareerBuddyAIPage from '@/pages/CareerBuddyAIPage';
import ActivateAccountPage from '@/pages/ActivateAccountPage';
import AcceptAgreementsPage from '@/pages/AcceptAgreementsPage';
import ParentConsentPage from '@/pages/ParentConsentPage';
import ParentSafetyConcerns from '@/pages/parent/ParentSafetyConcerns';
import ParentPrivacyPermissions from '@/pages/parent/ParentPrivacyPermissions';
import InternshipOpportunityPage from '@/pages/InternshipOpportunityPage';
import PartnerInvitePage from '@/pages/PartnerInvitePage';
import PartnerDashboard from '@/pages/partner/PartnerDashboard';
import PartnerOfferEditor from '@/pages/partner/PartnerOfferEditor';
import PartnerProgramWorkspace from '@/pages/partner/PartnerProgramWorkspace';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/activate-account" element={<ActivateAccountPage />} />
            <Route path="/accept-agreements" element={<AcceptAgreementsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/for-schools" element={<ForSchoolsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/student-safety" element={<StudentSafetyPage />} />
            <Route path="/school-registration" element={<SchoolRegistrationPage />} />
            <Route path="/bring-careerspov" element={<BringToSchoolPage />} />
            <Route path="/internships/offer" element={<InternshipOpportunityPage />} />
            <Route path="/internships/invite/:token" element={<InternshipOpportunityPage />} />
            <Route path="/internships/opportunity/:token" element={<InternshipOpportunityPage />} />
            <Route path="/partner/invite/:token" element={<PartnerInvitePage />} />
            {['/partner', '/partner/programs', '/partner/company'].map(path => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute roles={['partner']}>
                    <PartnerDashboard />
                  </ProtectedRoute>
                }
              />
            ))}
            <Route
              path="/partner/programs/:programId"
              element={
                <ProtectedRoute roles={['partner']}>
                  <PartnerProgramWorkspace />
                </ProtectedRoute>
              }
            />
            <Route path="/partner/interns" element={<Navigate to="/partner/programs" replace />} />
            <Route path="/partner/shortlisted" element={<Navigate to="/partner/programs" replace />} />
            <Route path="/partner/offers" element={<Navigate to="/partner/programs" replace />} />
            <Route
              path="/partner/offers/new"
              element={
                <ProtectedRoute roles={['partner']}>
                  <PartnerOfferEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner/offers/:offerId"
              element={
                <ProtectedRoute roles={['partner']}>
                  <PartnerOfferEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coordinator"
              element={
                <ProtectedRoute roles={['coordinator']}>
                  <CoordinatorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coordinator/mentors/:mentorId"
              element={
                <ProtectedRoute roles={['coordinator']}>
                  <MentorDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent"
              element={
                <ProtectedRoute roles={['parent']}>
                  <ParentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/children"
              element={
                <ProtectedRoute roles={['parent']}>
                  <ParentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/experiences"
              element={
                <ProtectedRoute roles={['parent']}>
                  <ParentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/internships"
              element={
                <ProtectedRoute roles={['parent']}>
                  <ParentDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/parent/consent"
              element={
                <ProtectedRoute roles={['parent']}>
                  <ParentConsentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/privacy-permissions"
              element={
                <ProtectedRoute roles={['parent']}>
                  <ParentPrivacyPermissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/safety-concerns"
              element={
                <ProtectedRoute roles={['parent']}>
                  <ParentSafetyConcerns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/children/:childId"
              element={
                <ProtectedRoute roles={['parent']}>
                  <ChildJourneyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/children/:childId/career-buddy-ai"
              element={
                <ProtectedRoute roles={['parent']}>
                  <CareerBuddyAIPage />
                </ProtectedRoute>
              }
            />
            <Route path="/volunteer/:token" element={<VolunteerPage />} />
            <Route path="/careers" element={<ExploreCareersPage />} />
            <Route path="/careers/:slug" element={<CareerDetailPage />} />
            <Route
              path="/mentor"
              element={
                <ProtectedRoute roles={['parent']}>
                  <MentorHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mentor/experiences"
              element={
                <ProtectedRoute roles={['parent']}>
                  <MentorExperiencesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mentor/profile"
              element={
                <ProtectedRoute roles={['parent']}>
                  <MentorProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mentor/onboarding"
              element={
                <ProtectedRoute roles={['parent']}>
                  <MentorOnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/careers/:slug/book"
              element={
                <ProtectedRoute roles={['parent']}>
                  <BookingFlowPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sessions/:bookingId"
              element={
                <ProtectedRoute roles={['parent', 'coordinator', 'admin']}>
                  <SessionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/group-sessions/:sessionId"
              element={
                <ProtectedRoute roles={['parent', 'coordinator', 'admin']}>
                  <GroupSessionDetailPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Layout>
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
